import { Ajax, AjaxError, ISupportSpinner } from '../shared/Ajax'

import { ApplicantModel } from '../models/ApplicantModel'
import { ApplicationModel } from '../models/ApplicationModel'
import { ApplicationPayload } from '../models/ApplicationPayload'
import { ApplicationRefresh } from '../models/ApplicationRefresh'
import { ApplicationSubmission } from '../models/ApplicationSubmission'
import { BSB } from '../models/BSB'
import { DashboardModel } from '../models/DashboardModel'
import { DropdownPair } from '../models/DropdownPair'
import { EligibilityValidationModel } from '../models/EligibilityValidationModel'
import { GrantAgreement } from '../models/GrantAgreement'
import { GrantAgreementHistoryModel } from '../models/GrantAgreementHistoryModel'
import { GrantAgreementModel } from '../models/GrantAgreementModel'
import { GrantAgreementSummary } from '../models/GrantAgreementSummary'
import { ReportingRequirementSummary } from '../models/ReportingRequirementSummary'
import { SubmissionDTO } from '../models/SubmissionDTO'
import { TierYearlyLimitEligibilityModel } from '../models/TierYearlyLimitEligibilityModel'

export class ApplicationController extends Ajax {
  constructor() {
    super(window.__emdgBaseUrl, '/api/application')
  }

  prepopulate(owner:ISupportSpinner, model:ApplicationModel, success:{(res:ApplicationModel):any}, error?:AjaxError) {
    super._call('post', 'prepopulate', owner, null, model, success, error)
  }

  create(owner:ISupportSpinner, success:{(res:string):any}, error?:AjaxError) {
    super._call('post', '', owner, null, null, success, error, 'text')
  }

  async getItem(owner:ISupportSpinner, id:string, success:{(res:ApplicationPayload):any}, error?:AjaxError) {
    super._call('get', '', owner, { id }, null, success, error)
  }

  getApplicantDetails(owner:ISupportSpinner, success:{(res:ApplicantModel):any}, error?:AjaxError) {
    super._call('get', 'GetApplicantDetails', owner, null, null, success, error)
  }

  updateApplication(owner:ISupportSpinner, model:ApplicationModel, success:{(res:ApplicationModel):any}, error?:AjaxError) {
    super._call('put', 'UpdateApplication', owner, null, model, success, error)
  }

  submit(owner:ISupportSpinner, submission:ApplicationSubmission, success:{(res:SubmissionDTO):any}, error?:AjaxError) {
    super._call('put', 'submit', owner, null, submission, success, error)
  }

  delete(owner:ISupportSpinner, applicationId:string, success:{():any}, error?:AjaxError) {
    super._call('delete', 'Delete', owner, { applicationId }, null, success, error, 'text')
  }

  updateRelatedCompanies(owner:ISupportSpinner, model:ApplicationModel, success:{(res:ApplicationModel):any}, error?:AjaxError) {
    super._call('put', 'UpdateRelatedCompanies', owner, null, model, success, error)
  }

  updateCompanyHistory(owner:ISupportSpinner, model:ApplicationModel, success:{(res:ApplicationModel):any}, error?:AjaxError) {
    super._call('put', 'UpdateCompanyHistory', owner, null, model, success, error)
  }

  updateBusinessPartners(owner:ISupportSpinner, model:ApplicationModel, success:{(res:ApplicationModel):any}, error?:AjaxError) {
    super._call('put', 'UpdateBusinessPartners', owner, null, model, success, error)
  }

  updatePlannedExpenditures(owner:ISupportSpinner, model:ApplicationModel, success:{(res:ApplicationModel):any}, error?:AjaxError) {
    super._call('put', 'UpdatePlannedExpenditures', owner, null, model, success, error)
  }

  bsbSearch(owner:ISupportSpinner, searchTxt:string, success:{(res:BSB):any}, error?:AjaxError) {
    super._call('get', 'bsbsearch', owner, { searchTxt }, null, success, error)
  }

  getCurrentList(owner:ISupportSpinner, success:{(res:DashboardModel):any}, error?:AjaxError) {
    super._call('get', 'GetCurrentList', owner, null, null, success, error)
  }

  getDashBoardDetailByApplicationId(owner:ISupportSpinner, applicationID:string, success:{(res:DashboardModel):any}, error?:AjaxError) {
    super._call('get', 'GetDashBoardDetailByApplicationId', owner, { applicationID }, null, success, error)
  }

  getHistoryList(owner:ISupportSpinner, success:{(res:DashboardModel):any}, error?:AjaxError) {
    super._call('get', 'GetHistoryList', owner, null, null, success, error)
  }

  grantAgreementAccepted(owner:ISupportSpinner, model:GrantAgreementModel, success:{(res:string):any}, error?:AjaxError) {
    super._call('post', 'GrantAgreementAccepted', owner, null, model, success, error, 'text')
  }

  grantAgreementDeclined(owner:ISupportSpinner, model:GrantAgreementModel, success:{(res:string):any}, error?:AjaxError) {
    super._call('post', 'GrantAgreementDeclined', owner, null, model, success, error, 'text')
  }

  getGrantAgreementHistory(owner:ISupportSpinner, claimRandomIdentifier:string, success:{(res:GrantAgreementHistoryModel[]):any}, error?:AjaxError) {
    super._call('get', 'GetGrantAgreementHistory', owner, { claimRandomIdentifier }, null, success, error)
  }

  isActiveGrantAgreement(owner:ISupportSpinner, abn:string, success:{(res:boolean):any}, error?:AjaxError) {
    super._call('get', 'IsActiveGrantAgreement', owner, { abn }, null, success, error)
  }

  validateEmailForAbn(owner:ISupportSpinner, abn:string, email:string, success:{(res:boolean):any}, error?:AjaxError) {
    super._call('get', 'validateEmailForAbn', owner, { abn, email }, null, success, error)
  }

  getFinancialYearId(owner:ISupportSpinner, finYearName:string, success:{(res:string):any}, error?:AjaxError) {
    super._call('get', 'GetFinancialYearId', owner, { finYearName }, null, success, error, 'text')
  }

  getFinYearsGuid(owner:ISupportSpinner, lastFinYear:string, thisFinYear:string, success:{(res:DropdownPair[]):any}, error?:AjaxError) {
    super._call('get', 'getFinYearsGuid', owner, { lastFinYear, thisFinYear }, null, success, error)
  }

  getExpenditureYearsForDropdown(owner:ISupportSpinner, appTier:number, success:{(res:DropdownPair[]):any}, error?:AjaxError) {
    super._call('get', 'getExpenditureYearsForDropdown', owner, { appTier }, null, success, error)
  }
  
  getGrantAgreementSummary(owner:ISupportSpinner, applicationId:string, userId:string, grantAgreementModel:GrantAgreement, success:{(res:GrantAgreementSummary):any}, error?:AjaxError) {
    super._call('get', '', owner, { applicationId, userId }, grantAgreementModel, success, error)
  }

  getReportingRequirementSummary(owner:ISupportSpinner, applicationId:string, userId:string, grantAgreementModel:GrantAgreement, success:{(res:ReportingRequirementSummary):any}, error?:AjaxError) {
    super._call('get', '', owner, { applicationId, userId }, grantAgreementModel, success, error)
  }

  isActiveGrantAgreementForApplication(owner:ISupportSpinner, abn:string, success:{(res:boolean):any}, error?:AjaxError) {
    super._call('get', '', owner, { abn }, null, success, error)
  }

  validateTierYearlyLimitEligibility(
    owner:ISupportSpinner,
    model: {abn:string, financialYearName:string, isRepresentativeBody:boolean, appTier?:number},
    success:{(res:TierYearlyLimitEligibilityModel):any},
    error?:AjaxError,
  ) {
    super._call('get', 'validateTierYearlyLimitEligibility', owner, model, null, success, error)
  }

  validateAnnualTurnoverEligibility(
    owner: ISupportSpinner,
    model: { totalTurnover: number, grantProgramRound: string, isRepresentativeBody: boolean, appTier: string },
    success: { (res: EligibilityValidationModel): any },
    error?: AjaxError,
  ) {
    super._call('get', 'validateAnnualTurnoverEligibility', owner, model, null, success, error)
  }

  validateGrantHistoryEligibility(
    owner: ISupportSpinner,
    model: { abn: string },
    success: { (res: EligibilityValidationModel): any },
    error?: AjaxError,
  ) {
    super._call('get', 'validateGrantHistoryEligibility', owner, model, null, success, error)
  }

  validateYearsInBusinessEligibility(
    owner: ISupportSpinner,
    model: { dateOperationsCommenced: string, grantProgramRound: string, isRepresentativeBody: boolean, appTier: string },
    success: { (res: EligibilityValidationModel): any },
    error?: AjaxError,
  ) {
    super._call('get', 'validateYearsInBusinessEligibility', owner, model, null, success, error)
  }
}
